import React, { useState, useEffect, useMemo } from 'react';
import { collection, query, orderBy, onSnapshot, addDoc, serverTimestamp, deleteDoc, doc, setDoc, updateDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { hashApiKey } from '../lib/apiKeys';
import { useAuth } from '../lib/AuthContext';
import { ConfirmModal, Toast } from '../components/InAppNotification';
import UpgradePromptModal from '../components/UpgradePromptModal';

export default function AgentsPage({ onViewChange, onlineAgents = [] }) {
    const { user, planLimits } = useAuth();
    const [agentKeys, setAgentKeys] = useState([]);
    const [isGeneratingKey, setIsGeneratingKey] = useState(false);
    const [copiedKeyId, setCopiedKeyId] = useState(null);
    const [upgradeModal, setUpgradeModal] = useState({ isOpen: false, title: '', message: '' });
    const [editingAgentId, setEditingAgentId] = useState(null);
    const [tempAgentName, setTempAgentName] = useState('');
    const [tempAgentRegion, setTempAgentRegion] = useState('');
    const [toast, setToast] = useState(null);
    const [allAgents, setAllAgents] = useState([]);
    const [confirmState, setConfirmState] = useState({ isOpen: false, title: '', message: '', onConfirm: null });
    const [filterStatus, setFilterStatus] = useState('all'); // 'all', 'online', 'offline'
    const [filterType, setFilterType] = useState('all'); // 'all', 'agent', 'extension'
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [geoCache, setGeoCache] = useState({});

    // Real-time listener on agent API keys
    useEffect(() => {
        if (!user) return;

        const keysRef = collection(db, 'users', user.uid, 'agentKeys');
        const q = query(keysRef, orderBy('createdAt', 'desc'));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            setAgentKeys(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
        }, (err) => {
            console.error("[Agents] Keys listener error:", err);
        });

        return () => unsubscribe();
    }, [user]);

    // Real-time listener on all registered agents
    useEffect(() => {
        if (!user) return;
        const q = query(collection(db, 'users', user.uid, 'agents'));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            setAllAgents(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
        });
        return () => unsubscribe();
    }, [user]);

    const handleResetAgentIdentity = (agentId) => {
        setConfirmState({
            isOpen: true,
            title: 'Refresh Node ID',
            message: 'This will force the agent to clear its local identity and restart with a fresh ID. Proceed?',
            onConfirm: async () => {
                try {
                    await updateDoc(doc(db, 'users', user.uid, 'agents', agentId), {
                        resetIdentity: true,
                        updatedAt: serverTimestamp()
                    });
                    setToast({ message: 'Reset signal sent to agent.', type: 'success' });
                } catch (err) {
                    setToast({ message: 'Failed to send reset signal.', type: 'error' });
                }
                setConfirmState(prev => ({ ...prev, isOpen: false }));
            }
        });
    };

    const handleDeleteAgent = (agentId) => {
        setConfirmState({
            isOpen: true,
            title: 'Delete Offline Agent',
            message: 'Are you sure you want to remove this agent record? This cannot be undone.',
            onConfirm: async () => {
                try {
                    await deleteDoc(doc(db, 'users', user.uid, 'agents', agentId));
                    setToast({ message: 'Agent record removed.', type: 'info' });
                } catch (err) {
                    setToast({ message: 'Failed to delete agent.', type: 'error' });
                }
                setConfirmState(prev => ({ ...prev, isOpen: false }));
            }
        });
    };

    const mergedAgents = useMemo(() => {
        const agents = allAgents.map(agent => ({
            ...agent,
            isOnline: onlineAgents.some(oa => oa.id === agent.id)
        }));

        return agents.sort((a, b) => {
            // First sort by online status
            if (a.isOnline !== b.isOnline) {
                return b.isOnline ? 1 : -1;
            }
            // Then sort by updatedAt or createdAt
            const timeA = a.updatedAt?.seconds || a.createdAt?.seconds || 0;
            const timeB = b.updatedAt?.seconds || b.createdAt?.seconds || 0;
            return timeB - timeA;
        });
    }, [allAgents, onlineAgents]);

    const filteredAgents = useMemo(() => {
        let result = mergedAgents;
        if (filterStatus !== 'all') {
            const wantOnline = filterStatus === 'online';
            result = result.filter(a => a.isOnline === wantOnline);
        }
        if (filterType !== 'all') {
            const wantExtension = filterType === 'extension';
            result = result.filter(a => (a.type === 'browser-extension') === wantExtension);
        }
        return result;
    }, [mergedAgents, filterStatus, filterType]);

    // Simple Geolocation resolution (IP -> Region)
    useEffect(() => {
        const fetchGeo = async (ip) => {
            if (!ip || ip === '0.0.0.0' || ip === 'unknown' || geoCache[ip]) return;
            try {
                const resp = await fetch(`https://ipapi.co/${ip}/json/`);
                const data = await resp.json();
                if (data.city && data.region_code) {
                    setGeoCache(prev => ({ ...prev, [ip]: `${data.city}, ${data.region_code}` }));
                } else if (data.country_name) {
                    setGeoCache(prev => ({ ...prev, [ip]: data.country_name }));
                }
            } catch (e) {
                console.warn("Geo lookup failed for", ip);
            }
        };

        filteredAgents.forEach(agent => {
            if (agent.publicIp) fetchGeo(agent.publicIp);
        });
    }, [filteredAgents]);

    const handleGenerateKey = async () => {
        setIsGeneratingKey(true);
        // Enforce Local Agent limits based on plan
        const maxAgents = planLimits?.maxLocalAgents ?? 1;
        if (agentKeys.length >= maxAgents) {
            setUpgradeModal({
                isOpen: true,
                title: 'Agent Limit Reached',
                message: `Your current plan restricts you to ${maxAgents} local agent${maxAgents === 1 ? '' : 's'}. Upgrade to a higher tier to add unlimited and parallel testing nodes.`
            });
            setIsGeneratingKey(false);
            return;
        }

        try {
            const array = new Uint8Array(16);
            crypto.getRandomValues(array);
            const keyStr = Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
            const keyHash = await hashApiKey(keyStr);

            const keyRef = await addDoc(collection(db, 'users', user.uid, 'agentKeys'), {
                key: keyStr,
                keyHash,
                name: `Agent Key ${agentKeys.length + 1}`,
                active: true,
                createdAt: serverTimestamp(),
                lastUsedAt: null
            });

            await setDoc(doc(db, 'agentKeysLookup', keyHash), {
                uid: user.uid,
                keyId: keyRef.id,
                keyHash,
                active: true,
                createdAt: serverTimestamp(),
                lastUsedAt: null
            });
            setToast({ message: 'New API Key generated successfully.', type: 'success' });
        } catch (err) {
            console.error('Failed to generate key', err);
            setToast({ message: 'Failed to generate key.', type: 'error' });
        } finally {
            setIsGeneratingKey(false);
        }
    };

    const handleRevokeKey = (keyId, keyStr) => {
        setConfirmState({
            isOpen: true,
            title: 'Revoke API Key',
            message: 'Are you sure you want to revoke this key? Any agents using it will be instantly disconnected.',
            onConfirm: async () => {
                try {
                    await deleteDoc(doc(db, 'users', user.uid, 'agentKeys', keyId));
                    await Promise.all([
                        deleteDoc(doc(db, 'agentKeysLookup', await hashApiKey(keyStr))),
                        deleteDoc(doc(db, 'agentKeysLookup', keyStr))
                    ]);
                    setToast({ message: 'API Key revoked.', type: 'info' });
                } catch (err) {
                    console.error('Failed to revoke key', err);
                    setToast({ message: 'Failed to revoke key.', type: 'error' });
                }
                setConfirmState(prev => ({ ...prev, isOpen: false }));
            }
        });
    };

    const handleCopyKey = (keyId, keyStr) => {
        navigator.clipboard.writeText(keyStr);
        setCopiedKeyId(keyId);
        setTimeout(() => setCopiedKeyId(null), 2000);
    };

    const handleSaveAgentMetadata = async (agentId, newName, newRegion) => {
        try {
            await updateDoc(doc(db, 'users', user.uid, 'agents', agentId), {
                customName: newName,
                assignedRegion: newRegion,
                updatedAt: serverTimestamp()
            });
            setToast({ message: 'Agent metadata updated.', type: 'success' });
            setEditingAgentId(null);
        } catch (err) {
            console.error('Failed to update agent metadata:', err);
            setToast({ message: 'Failed to update metadata.', type: 'error' });
        }
    };

    const toggleAgentSelection = (agentId) => {
        const newSelection = new Set(selectedIds);
        if (newSelection.has(agentId)) newSelection.delete(agentId);
        else newSelection.add(agentId);
        setSelectedIds(newSelection);
    };

    const handleBulkDelete = () => {
        if (selectedIds.size === 0) return;
        setConfirmState({
            isOpen: true,
            title: 'Bulk Delete Scanners',
            message: `Are you sure you want to remove ${selectedIds.size} scanner records? This cannot be undone.`,
            onConfirm: async () => {
                try {
                    const deletes = Array.from(selectedIds).map(id => deleteDoc(doc(db, 'users', user.uid, 'agents', id)));
                    await Promise.all(deletes);
                    setSelectedIds(new Set());
                    setToast({ message: `${selectedIds.size} records removed.`, type: 'info' });
                } catch (err) {
                    setToast({ message: 'Failed to delete some records.', type: 'error' });
                }
                setConfirmState(prev => ({ ...prev, isOpen: false }));
            }
        });
    };

    return (
        <div className="p-10 space-y-10 animate-in fade-in slide-in-from-right-4 duration-500 max-w-7xl mx-auto">
            <header className="flex items-end justify-between">
                <div>
                    <h1 className="text-4xl font-black text-(--text) tracking-tighter uppercase mb-2">Scanners</h1>
                    <div className="flex items-center gap-3">
                        <span className="w-1.5 h-1.5 bg-(--accent) rounded-full animate-ping"></span>
                        <span className="text-xs font-bold text-(--accent) tracking-[0.3em] uppercase opacity-60">Compute Network Command</span>
                    </div>
                </div>
                <div className="flex items-center gap-4">
                    {selectedIds.size > 0 && (
                        <button
                            onClick={handleBulkDelete}
                            className="bg-fail text-white px-6 py-3 rounded-full text-[0.625rem] font-black uppercase tracking-widest shadow-lg hover:bg-fail/80 transition-all flex items-center gap-2"
                        >
                            <span>🗑️</span> Delete Selected ({selectedIds.size})
                        </button>
                    )}
                    <a
                        href="https://github.com/stamandster/A11y/releases/download/v1.0.0/A11y-Scout-Agent-Setup.exe"
                        className="bg-(--surface-elevated) border-2 border-(--accent) text-(--accent) px-6 py-3 rounded-full text-[0.625rem] font-black uppercase tracking-widest shadow-[0_0_1.25em_rgba(var(--accent-rgb),0.1)] hover:bg-(--accent) hover:text-black transition-all flex items-center gap-2"
                        title="Download Windows Agent"
                    >
                        <span>⬇️</span> Download Agent
                    </a>
                    <button
                        disabled={isGeneratingKey}
                        onClick={handleGenerateKey}
                        className="pill-button !py-3 !px-8 flex items-center gap-3 disabled:opacity-50"
                    >
                        {isGeneratingKey ? (
                            <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        ) : (
                            <span>🔑</span>
                        )}
                        Generate New API Key
                    </button>
                </div>
            </header>

            <div className="flex flex-wrap items-center gap-6 pb-2">
                <div className="flex items-center gap-3">
                    <span className="text-[0.625rem] font-black uppercase tracking-widest text-(--text)/40">Status</span>
                    <div className="flex bg-stone/50 p-1 rounded-xl border border-(--text)/5">
                        {['all', 'online', 'offline'].map(s => (
                            <button
                                key={s}
                                onClick={() => setFilterStatus(s)}
                                className={`px-4 py-1.5 rounded-lg text-[0.5625rem] font-black uppercase tracking-widest transition-all ${filterStatus === s ? 'bg-white text-(--text) shadow-sm' : 'text-(--text)/40 hover:text-(--text)'}`}
                            >
                                {s}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="flex items-center gap-3">
                    <span className="text-[0.625rem] font-black uppercase tracking-widest text-(--text)/40">Type</span>
                    <div className="flex bg-stone/50 p-1 rounded-xl border border-(--text)/5">
                        {['all', 'agent', 'extension'].map(t => (
                            <button
                                key={t}
                                onClick={() => setFilterType(t)}
                                className={`px-4 py-1.5 rounded-lg text-[0.5625rem] font-black uppercase tracking-widest transition-all ${filterType === t ? 'bg-white text-(--text) shadow-sm' : 'text-(--text)/40 hover:text-(--text)'}`}
                            >
                                {t}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Online Agents List */}
                <div className="lg:col-span-2 space-y-6">
                    <div className="flex items-center justify-between">
                        <h3 className="text-xs font-black uppercase tracking-[0.2em] text-(--text)/40 flex items-center gap-3">
                            <span className="w-1.5 h-1.5 bg-(--accent) rounded-full animate-pulse" />
                            {filterStatus === 'online' ? 'Online' : filterStatus === 'offline' ? 'Offline' : 'All'} Scanners ({filteredAgents.length})
                        </h3>
                        {filteredAgents.length > 0 && (
                            <button
                                onClick={() => {
                                    if (selectedIds.size === filteredAgents.length) setSelectedIds(new Set());
                                    else setSelectedIds(new Set(filteredAgents.map(a => a.id)));
                                }}
                                className="text-[0.5625rem] font-black uppercase tracking-widest text-(--accent) hover:underline"
                            >
                                {selectedIds.size === filteredAgents.length ? 'Deselect All' : 'Select All'}
                            </button>
                        )}
                    </div>

                    <div className="space-y-4">
                        {filteredAgents.length > 0 ? (
                            filteredAgents.map(agent => (
                                <div
                                    key={agent.id}
                                    className={`bento-card p-6 flex flex-col justify-between group transition-all relative overflow-hidden ${!agent.isOnline ? 'opacity-60 saturate-50' : ''} ${selectedIds.has(agent.id) ? 'border-(--accent) ring-1 ring-(--accent)/30 shadow-md' : ''}`}
                                    onClick={(e) => {
                                        if (e.target.closest('button') || e.target.closest('input')) return;
                                        toggleAgentSelection(agent.id);
                                    }}
                                >
                                    <div className="flex items-start gap-4">
                                        <div onClick={(e) => e.stopPropagation()} className="pt-1">
                                            <input
                                                type="checkbox"
                                                checked={selectedIds.has(agent.id)}
                                                onChange={() => toggleAgentSelection(agent.id)}
                                                className="w-4 h-4 rounded border-(--text)/10 text-(--accent) focus:ring-(--accent) cursor-pointer"
                                            />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-start justify-between mb-4">
                                                <div className="flex-1 min-w-0">
                                                    {editingAgentId === agent.id ? (
                                                        <div className="flex flex-col gap-2 p-1 bg-stone/5 rounded-xl border border-(--text)/5">
                                                            <div className="flex items-center gap-2">
                                                                <span className="text-[0.625rem] font-black uppercase tracking-widest text-espresso/40 w-12">Name</span>
                                                                <input
                                                                    autoFocus
                                                                    value={tempAgentName}
                                                                    onChange={(e) => setTempAgentName(e.target.value)}
                                                                    placeholder="Friendly Name"
                                                                    className="bg-white border border-espresso/10 rounded px-2 py-1 text-xs font-bold flex-1 focus:outline-none focus:border-teal"
                                                                    onClick={(e) => e.stopPropagation()}
                                                                />
                                                            </div>
                                                            <div className="flex items-center gap-2">
                                                                <span className="text-[0.625rem] font-black uppercase tracking-widest text-espresso/40 w-12">Region</span>
                                                                <input
                                                                    value={tempAgentRegion}
                                                                    onChange={(e) => setTempAgentRegion(e.target.value)}
                                                                    placeholder="e.g. US, EU, APAC"
                                                                    className="bg-white border border-espresso/10 rounded px-2 py-1 text-xs font-bold flex-1 focus:outline-none focus:border-teal"
                                                                    onClick={(e) => e.stopPropagation()}
                                                                />
                                                            </div>
                                                            <div className="flex justify-end gap-2 pt-1 border-t border-(--text)/5">
                                                                <button 
                                                                    onClick={(e) => { e.stopPropagation(); setEditingAgentId(null); }} 
                                                                    className="text-[0.625rem] font-black uppercase tracking-widest text-fail px-3 py-1 hover:bg-fail/5 rounded-lg transition-all"
                                                                >
                                                                    Cancel
                                                                </button>
                                                                <button 
                                                                    onClick={(e) => { e.stopPropagation(); handleSaveAgentMetadata(agent.id, tempAgentName, tempAgentRegion); }} 
                                                                    className="text-[0.625rem] font-black uppercase tracking-widest bg-(--pass-green)/10 text-(--text) border border-(--pass-green)/30 px-4 py-1.5 rounded-lg shadow-sm hover:bg-(--pass-green)/20 transition-all font-black"
                                                                >
                                                                    Save Changes
                                                                </button>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <div className="flex flex-col gap-1">
                                                            <div className="flex items-center gap-2">
                                                                <span className="text-sm font-black text-espresso truncate">
                                                                    {agent.customName || agent.osInfo?.hostname || agent.hostname || agent.id.slice(0, 8)}
                                                                </span>
                                                                <span className={`text-[0.5rem] px-1.5 py-0.5 rounded-md font-black tracking-widest ${agent.type === 'browser-extension' ? 'bg-(--accent)/10 text-(--accent)' : 'bg-(--text)/10 text-(--text)/60'}`}>
                                                                    {agent.type === 'browser-extension' ? 'EXTENSION' : 'AGENT'}
                                                                </span>
                                                                <button
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        setEditingAgentId(agent.id);
                                                                        setTempAgentName(agent.customName || agent.osInfo?.hostname || agent.hostname || agent.id.slice(0, 8));
                                                                        setTempAgentRegion(agent.assignedRegion || '');
                                                                    }}
                                                                    className="text-[0.625rem] opacity-0 group-hover:opacity-100 uppercase font-black text-(--accent) transition-opacity"
                                                                >
                                                                    Edit
                                                                </button>
                                                                <button
                                                                    onClick={(e) => { e.stopPropagation(); handleResetAgentIdentity(agent.id); }}
                                                                    className="text-[0.625rem] opacity-0 group-hover:opacity-100 uppercase font-black text-espresso/40 hover:text-fail transition-all"
                                                                >
                                                                    Refresh ID
                                                                </button>
                                                            </div>
                                                            <div className="flex items-center gap-4 mt-1">
                                                                <div className="flex items-center gap-1.5 px-2 py-0.5 rounded-lg bg-stone/5 border border-espresso/5 shadow-sm">
                                                                    <span className="text-[9px] font-black uppercase tracking-tighter text-espresso/40">Active</span>
                                                                    <span className="text-[10px] font-bold text-espresso">{agent.activeJobs || 0}</span>
                                                                </div>
                                                                <div className="flex items-center gap-1.5 px-2 py-0.5 rounded-lg bg-stone/5 border border-espresso/5 shadow-sm">
                                                                    <span className="text-[9px] font-black uppercase tracking-tighter text-espresso/40">Pending</span>
                                                                    <span className="text-[10px] font-bold text-espresso">{agent.pendingJobs || 0}</span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}
                                                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1">
                                                        <p className="text-[0.625rem] font-bold text-espresso/40 uppercase tracking-widest">
                                                            {agent.osInfo?.platform || agent.platform || 'Node'} • {agent.osInfo?.arch || 'x64'}
                                                        </p>
                                                        {agent.publicIp && (
                                                            <div className="flex items-center gap-2">
                                                                <span className="text-espresso/20">•</span>
                                                                <span className="text-[0.5625rem] font-mono font-bold text-espresso/60 tracking-tight">IP: {agent.publicIp}</span>
                                                            </div>
                                                        )}
                                                        {agent.assignedRegion && (
                                                            <div className="flex items-center gap-2">
                                                                <span className="text-espresso/20">•</span>
                                                                <span className="px-1.5 py-0.5 rounded bg-(--text) text-white text-[0.5rem] font-black uppercase tracking-widest">
                                                                    🏳️ {agent.assignedRegion}
                                                                </span>
                                                            </div>
                                                        )}
                                                        {agent.publicIp && geoCache[agent.publicIp] && (
                                                            <div className="flex items-center gap-2">
                                                                <span className="text-espresso/20">•</span>
                                                                <span className="px-1.5 py-0.5 rounded bg-(--accent)/5 text-(--accent) text-[0.5rem] font-black uppercase tracking-widest">
                                                                    📍 {geoCache[agent.publicIp]}
                                                                </span>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="flex flex-col items-end gap-2">
                                                    <div className={`w-8 h-8 rounded-xl flex items-center justify-center text-xs ${agent.isOnline ? 'bg-(--pass-green)/10 text-(--pass-green)' : 'bg-(--fail-red)/10 text-(--fail-red)'}`}>
                                                        {agent.isOnline ? '⚡' : '💤'}
                                                    </div>
                                                    {!agent.isOnline && (
                                                        <button
                                                            onClick={(e) => { e.stopPropagation(); handleDeleteAgent(agent.id); }}
                                                            className="text-[0.5rem] font-black uppercase bg-fail/10 text-espresso border border-fail/30 px-3 py-1 rounded-lg opacity-0 group-hover:opacity-100 transition-all hover:bg-fail/20"
                                                        >
                                                            Delete
                                                        </button>
                                                    )}
                                                </div>
                                            </div>

                                            <div className="space-y-2">
                                                <div className="flex items-center justify-between text-[0.5625rem] font-black uppercase tracking-widest text-espresso/30">
                                                    <span>Status</span>
                                                    <span className={agent.isOnline ? 'text-(--pass-green)' : 'text-(--fail-red)'}>
                                                        {agent.isOnline ? 'Operational' : 'Offline'}
                                                    </span>
                                                </div>
                                                <div className="h-1 bg-stone/50 rounded-full overflow-hidden">
                                                    <div className={`h-full transition-all duration-1000 ${agent.isOnline ? 'bg-(--pass-green) w-full animate-pulse' : 'bg-stone/50 w-0'}`} />
                                                </div>
                                                <p className="text-[0.5625rem] font-bold text-espresso/40">
                                                    {agent.isOnline
                                                        ? `Active since ${agent.connectedAt?.toDate ? agent.connectedAt.toDate().toLocaleDateString() : 'Initial Boot'}`
                                                        : `Last seen ${agent.updatedAt?.toDate ? agent.updatedAt.toDate().toLocaleString() : 'N/A'}`
                                                    }
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="col-span-full py-20 text-center bento-card border-dashed">
                                <div className="text-4xl mb-4 opacity-20">📡</div>
                                <p className="text-xs font-black uppercase tracking-widest text-espresso/30">No active scanners detected</p>
                                <p className="text-[0.625rem] font-bold text-espresso/20 mt-2">Connect a local agent using an API key below</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* API Keys Management */}
                <div className="space-y-6">
                    <h3 className="text-xs font-black uppercase tracking-[0.2em] text-espresso/40 flex items-center gap-3">
                        <span className="w-1.5 h-1.5 bg-(--text)/20 rounded-full" />
                        Security Credentials
                    </h3>

                    <div className="space-y-4">
                        {agentKeys.map(key => (
                            <div key={key.id} className="bento-card p-6 space-y-4">
                                <div className="flex items-center justify-between">
                                    <span className="text-[0.625rem] font-black uppercase tracking-widest text-espresso/60">{key.name}</span>
                                    <button
                                        onClick={() => handleRevokeKey(key.id, key.key)}
                                        className="text-[0.625rem] font-black uppercase tracking-widest text-fail hover:underline"
                                    >
                                        Revoke
                                    </button>
                                </div>
                                <div className="flex items-center gap-2 bg-stone/30 p-3 rounded-xl border border-espresso/5">
                                    <code className="flex-1 text-[0.625rem] font-mono font-bold text-espresso/60 truncate tracking-tight">
                                        {key.key.slice(0, 4)}••••••••••{key.key.slice(-4)}
                                    </code>
                                    <button
                                        onClick={() => handleCopyKey(key.id, key.key)}
                                        className={`px-3 py-1 rounded-lg text-[0.5625rem] font-black uppercase tracking-widest transition-all ${copiedKeyId === key.id ? 'bg-(--pass-green) text-(--text)' : 'bg-(--accent)/10 text-(--accent) border border-(--accent)/20'}`}
                                    >
                                        {copiedKeyId === key.id ? 'Copied' : 'Copy'}
                                    </button>
                                </div>
                                <div className="flex items-center justify-between text-[0.5rem] font-black uppercase tracking-widest text-espresso/30">
                                    <span>Created {key.createdAt?.toDate ? key.createdAt.toDate().toLocaleDateString() : 'N/A'}</span>
                                    <span>{key.lastUsedAt ? `Last seen ${new Date(key.lastUsedAt.seconds * 1000).toLocaleDateString()}` : 'Never used'}</span>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="p-8 bg-[#1A1A1A] rounded-[1.5rem] text-white border border-espresso/10 shadow-2xl flex flex-col gap-6">
                        <div>
                            <h4 className="text-[0.625rem] font-black uppercase tracking-widest text-(--accent) mb-4">Local Agent Installer</h4>
                            <p className="text-[0.625rem] font-bold text-white/70 leading-relaxed mb-6">
                                Download the A11y Scout Local Agent to securely scan internal networks and localhost environments.
                            </p>
                            <a 
                                href="https://github.com/stamandster/A11y/releases/download/v1.0.0/A11y-Scout-Agent-Setup.exe" 
                                onClick={(e) => {
                                    // Placeholder if needed
                                    // e.preventDefault();
                                    // setToast({ message: 'Installer download will begin shortly.', type: 'info' });
                                }}
                                className="w-full bg-(--accent) text-black hover:bg-white hover:text-black hover:scale-105 transition-all text-[0.625rem] font-black uppercase tracking-widest px-6 py-4 rounded-xl flex items-center justify-center gap-3 shadow-lg"
                            >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"></path></svg>
                                Download Local Agent (.exe)
                            </a>
                        </div>
                        
                        <div className="border-t border-white/10 pt-6">
                            <h4 className="text-[0.625rem] font-black uppercase tracking-widest text-white/40 mb-4">Quick Setup Guide</h4>
                            <ol className="space-y-3">
                                <li className="flex gap-3">
                                    <span className="text-(--accent) font-black text-xs">01</span>
                                    <p className="text-[0.625rem] font-black leading-relaxed text-white/90">Copy an API key from above or generate a fresh one.</p>
                                </li>
                                <li className="flex gap-3">
                                    <span className="text-(--accent) font-black text-xs">02</span>
                                    <p className="text-[0.625rem] font-black leading-relaxed text-white/90">Launch your local agent executable with the key provided.</p>
                                </li>
                                <li className="flex gap-3">
                                    <span className="text-(--accent) font-black text-xs">03</span>
                                    <p className="text-[0.625rem] font-black leading-relaxed text-white/90">Scanner status will automatically update to 'Operational'.</p>
                                </li>
                            </ol>
                        </div>
                    </div>
                </div>
            </div>

            {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
            {confirmState.isOpen && <ConfirmModal {...confirmState} onCancel={() => setConfirmState(prev => ({ ...prev, isOpen: false }))} />}
            <UpgradePromptModal
                isOpen={upgradeModal.isOpen}
                onClose={() => setUpgradeModal({ isOpen: false, title: '', message: '' })}
                title={upgradeModal.title}
                message={upgradeModal.message}
            />
        </div>
    );
}
